/**
 * ColorblindFilterNode — TSL colour-matrix post step for colour-vision
 * deficiency correction (daltonization).
 *
 * Mirrors the integration shape of `createAces2ToneMapNode` in
 * `src/render/color/aces2.ts`: returns a callable `(rgb: Node) => Node` plus a
 * `strength` uniform so callers can lerp the correction in/out without
 * rebuilding the shader.
 *
 *   const filter = createColorblindFilterNode('deuteranopia');
 *   const corrected = filter.transform(sceneColorNode);
 *   filter.strength.value = 0.5; // live tweak
 *
 * The maths matches `daltonizeRgb()` in `./colorblindMatrices.ts`:
 *   err  = rgb - SIM·rgb
 *   out  = rgb + ERROR_SHIFT·err
 *   final = mix(rgb, out, strength)
 *
 * For 'achromatopsia' there is no remaining channel to redistribute into, so
 * the node lerps toward the luminance projection instead (same special-case
 * as the CPU reference).
 *
 * Note on matrix layout (verified against three r182):
 * TSL `mat3(n11..n33)` calls `new Matrix3(n11..n33)`, whose constructor/`set()`
 * interpret arguments in ROW-MAJOR order
 * (node_modules/three/src/math/Matrix3.js — `set()` writes te[0]=n11, te[1]=n21,
 * te[2]=n31, …, storing column-major elements). NodeBuilder.generateConst then
 * emits those column-major elements as `mat3(elements…)`, which GLSL/WGSL read
 * as columns — yielding exactly the matrix M with `out_i = Σ_j M[i][j]·rgb_j`.
 * Net effect: TSL `mat3()` already wants ROW-MAJOR scalars, so we feed our
 * row-major `Mat3x3` directly. (A transpose here would make the GPU apply Mᵀ
 * and diverge from the CPU `daltonizeRgb` reference.)
 */

import { mat3, vec3, mix, uniform } from 'three/tsl';
import type { ColorblindMode } from '../AccessibilityTypes.js';
import {
  type Mat3x3,
  simulationMatrix,
  DALTONIZE_ERROR_SHIFT,
  SIM_ACHROMATOPSIA,
} from './colorblindMatrices.js';

/** A TSL uniform node (exposes `.value`). */
interface UniformNode {
  value: number;
}

/**
 * Minimal structural view of a TSL colour/operand node. The engine performs
 * node math dynamically (see aces2.ts / LensFlareNode.ts); typing the handful
 * of operators we use keeps this file free of `any` without importing the full
 * TSL node type surface.
 */
interface NodeLike {
  mul(v: unknown): NodeLike;
  add(v: unknown): NodeLike;
  sub(v: unknown): NodeLike;
  readonly r: NodeLike;
  readonly g: NodeLike;
  readonly b: NodeLike;
}

interface Mat3Like {
  mul(v: unknown): NodeLike;
}

// TSL `mix`/`mat3` are strictly typed against MathNodeParameter; we thread our
// structural node types through narrow aliases.
const mixNode = mix as unknown as (a: NodeLike, b: NodeLike, t: UniformNode) => NodeLike;
const mat3Node = mat3 as unknown as (...c: number[]) => Mat3Like;

export interface ColorblindFilterNode {
  /** The CVD mode this filter was built for. */
  readonly mode: ColorblindMode;
  /** Strength uniform (0..1). Push changes via `.value`. */
  readonly strength: UniformNode;
  /** Apply the filter to an RGB colour node, returning the corrected node. */
  transform(rgb: unknown): unknown;
}

/**
 * Build a TSL `mat3` from a row-major `Mat3x3`.
 *
 * TSL `mat3()` forwards its scalars to `new Matrix3(...)`, whose constructor is
 * ROW-MAJOR (Matrix3.set: te[0]=n11, te[1]=n21, …). The resulting column-major
 * elements are then emitted verbatim and read column-major by the GPU, so the
 * shader matrix equals M and `m.mul(rgb)` computes the row-major product
 * `out_i = Σ_j M[i][j]·rgb_j`. We therefore pass our row-major coefficients
 * straight through — NO transpose (a transpose would apply Mᵀ on the GPU).
 *
 * Exported (not part of the public surface) so tests can verify the on-GPU
 * element order can never silently regress to the transpose.
 */
export function tslMat3FromRowMajor(m: Mat3x3): Mat3Like {
  return mat3Node(
    m[0], m[1], m[2], // row 0 = (M00, M01, M02)
    m[3], m[4], m[5], // row 1 = (M10, M11, M12)
    m[6], m[7], m[8], // row 2 = (M20, M21, M22)
  );
}

/**
 * Create a colourblind daltonization filter node.
 *
 * @param mode             CVD mode. 'none' returns a pass-through transform.
 * @param initialStrength  Initial strength (0..1). Default 1.
 */
export function createColorblindFilterNode(
  mode: ColorblindMode,
  initialStrength = 1,
): ColorblindFilterNode {
  const strength = uniform(Math.min(1, Math.max(0, initialStrength))) as unknown as UniformNode;

  if (mode === 'none') {
    return {
      mode,
      strength,
      transform: (rgb: unknown) => rgb,
    };
  }

  if (mode === 'achromatopsia') {
    const lumaMat = tslMat3FromRowMajor(SIM_ACHROMATOPSIA);
    return {
      mode,
      strength,
      transform: (rgb: unknown): unknown => {
        const c = rgb as NodeLike;
        const gray = lumaMat.mul(c);
        return mixNode(c, gray, strength);
      },
    };
  }

  const simMat = tslMat3FromRowMajor(simulationMatrix(mode));
  const shiftMat = tslMat3FromRowMajor(DALTONIZE_ERROR_SHIFT);

  return {
    mode,
    strength,
    transform: (rgb: unknown): unknown => {
      const c = rgb as NodeLike;
      const simulated = simMat.mul(c); // SIM·rgb
      const err = c.sub(simulated); // rgb - SIM·rgb
      const shifted = shiftMat.mul(err); // ERROR_SHIFT·err
      const corrected = c.add(shifted); // rgb + shift
      return mixNode(c, corrected, strength);
    },
  };
}

/**
 * Convenience: build a `vec3`-typed pass-through (used when a caller wants an
 * explicit identity node rather than the original node reference).
 */
export function identityColorNode(rgb: unknown): unknown {
  const c = rgb as NodeLike;
  // vec3 accepts node operands at runtime; its public typing only allows
  // numbers, so route through a node-typed alias.
  const vec3Node = vec3 as unknown as (r: unknown, g: unknown, b: unknown) => unknown;
  return vec3Node(c.r, c.g, c.b);
}
